import { useEffect, useState } from "react";
import { GridMetaData, createUrlSearchParams } from "models/grids";
import { TaskFilter } from "models/tasks";
import { useCentralApi, useCurrentUser } from "hooks/shared";
import { IWorkItemTask, TaskFocus } from "models/work-items/IWorkItemTask";

type GetWorkItemTasksResult = {
    tasks: IWorkItemTask[],
    total: number
}

type WorkItemTasksResult = [
    IWorkItemTask[],
    number,
    boolean,
    string | null,
];

type useWorkItemTasksSignature = (workItemId: number, status: TaskFilter, all: boolean, focus: TaskFocus, gridMetaData: GridMetaData, dependenciesLoaded: boolean, includeHierarchy?: boolean, isProject?: boolean) => WorkItemTasksResult;

const useWorkItemTasks: useWorkItemTasksSignature = (workItemId, status, all, focus, gridMetaData, dependenciesLoaded, includeHierarchy = false, isProject = false) => {
    const [ _lastQuery, _setLastQuery ] = useState<string>('');
    const [ result, setResult ] = useState<IWorkItemTask[]>([]);
    const [ total, setTotal ] = useState<number>(0);
    const [ error, setError ] = useState<string | null>(null);
    const [ loading, setLoading ] = useState<boolean>(true);
    const user = useCurrentUser();
    const fetcher = useCentralApi(user);

    useEffect(() => {
        if (workItemId === 0) return;

        const searchParams = createUrlSearchParams(gridMetaData);
        if (all) {
            searchParams.append('all', 'true');
        }
        if (focus) {
            searchParams.append('focus', focus);
        }
        if (includeHierarchy) {
            searchParams.append('incRel', 'true');
        }
        if (isProject) {
            searchParams.append('proj', 'true');
        }
        const query = `${workItemId}/tasks/${status}?${searchParams}`;

        if (_lastQuery === query) return;

        const controller = new AbortController();
        (async () => {
            if (user && user.id_token && dependenciesLoaded) {
                setLoading(true);

                try {
                    const resp = await fetcher(`/api/v3/workitems/${query}`,{
                        signal: controller.signal
                    });

                    if (!resp.ok) {
                        // RESPOND TO ERRORS
                    }

                    const data = await resp.json() as GetWorkItemTasksResult;
                    setResult(data.tasks);

                    if (data.total !== total) {
                        setTotal(data.total);
                    }

                    setResult(data.tasks);

                    _setLastQuery(query);
                } catch (err) {
                    console.log(err);
                } finally {
                    if (!controller.signal.aborted)
                        setLoading(false);
                }
            } else {
                // NOT LOGGED IN
                setError('Not logged in');
            }
        })();
        return () => {
            controller.abort();
            setLoading(false);
        }
    }, [ gridMetaData, workItemId, status, all, focus, dependenciesLoaded]);

    return [
        result,
        total,
        loading,
        error
    ];
};

export default useWorkItemTasks;